// frontend/src/pages/public/RequestVerified/RequestVerifiedPage.tsx
import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";

import "./RequestVerifiedPage.css";

type RouteParams = {
  manageToken?: string;
};

/**
 * Public page shown after email verification.
 *
 * The verify API redirects here:
 *   /public/request-verified/:manageToken
 */
export default function RequestVerifiedPage() {
  const { manageToken } = useParams<RouteParams>();

  const state = useMemo(() => {
    const t = (manageToken || "").trim();

    // Special sentinel routes used by the verify API for GET redirects on errors
    if (!t) return { kind: "missing" as const };
    if (t === "invalid") return { kind: "invalid" as const };
    if (t === "expired") return { kind: "expired" as const };
    if (t === "inactive") return { kind: "inactive" as const };

    return { kind: "ok" as const, token: t };
  }, [manageToken]);

  const manageHref =
    state.kind === "ok"
      ? `/public/manage-request/${encodeURIComponent(state.token)}`
      : null;

  return (
    <div className="rv-page">
      <PublicHeader variant="solid" />

      <main className="rv-container">
        <div className="rv-card">
          {state.kind === "ok" && (
            <>
              <div className="rv-pill rv-pill--success">✓ Email verified</div>

              <h1 className="rv-title">Your request has been verified</h1>

              <p className="rv-desc">
                Thank you — your email has been verified and your request has now been sent to the clinic for review.
              </p>

              <div className="rv-info-box">
                <div className="rv-info-title">What happens next</div>
                <ul className="rv-info-list">
                  <li>
                    The clinic will review your request based on its current capacity, practice scope, and intake criteria.
                  </li>
                  <li>
                    The clinic may invite you for a Meet & Greet, place your request on a waitlist, or be unable to accept your request. If you are invited, the clinic will contact you using the details you provided.
                  </li>
                  <li>
                    If the clinic does not take action within 7 days, the request will expire and you will be notified by email.
                  </li>
                  <li>You can safely close this page.</li>
                </ul>
              </div>

              <div className="rv-section">
                <div className="rv-section-title">Manage or cancel your request</div>
                <p className="rv-section-desc">
                  Use your secure manage link to check the status or cancel your request while it is active.
                </p>

                <div className="rv-actions">
                  {manageHref && (
                    <Link to={manageHref} className="rv-btn rv-btn-primary">
                      Manage request
                    </Link>
                  )}
                  <Link to="/public/how-it-works" className="rv-btn">
                    Learn how it works
                  </Link>
                </div>

                <p className="rv-help">
                  If you did not make this request, you can ignore this page. For
                  privacy, we do not display personal details here.
                </p>
              </div>
            </>
          )}

          {state.kind !== "ok" && (
            <>
              <div className="rv-pill rv-pill--warning">⚠ Verification issue</div>

              <h1 className="rv-title">We couldn't verify this request</h1>

              {state.kind === "expired" && (
                <p className="rv-desc">
                  This verification link has <strong>expired</strong>. Please
                  submit a new request and verify it within 24 hours.
                </p>
              )}

              {state.kind === "inactive" && (
                <p className="rv-desc">
                  This request is no longer active. It may have already been
                  cancelled, expired, or closed by the clinic.
                </p>
              )}

              {(state.kind === "invalid" || state.kind === "missing") && (
                <p className="rv-desc">
                  This verification link appears to be invalid. Please use the
                  latest link from your email.
                </p>
              )}

              <div className="rv-actions">
                <Link
                  to="/public/find-family-doctor"
                  className="rv-btn rv-btn-primary"
                >
                  Start a new request
                </Link>
                <Link to="/public/how-it-works" className="rv-btn">
                  Learn how it works
                </Link>
              </div>

              <p className="rv-help">
                If you keep getting this message, try opening the link from the
                same email again, or submit a new request.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
